/* 🔴 W46 — WHAT A TEMPO FIELD WILL ACCEPT. **The Swift twin is
 * `HybridTrainer/Services/TempoEntry.swift`, and it is the ORIGINAL** — the phone's tempo keypad
 * has enforced this rule since it was built. Both are driven by one generated case table
 * (`tools/w46-20260922/tempo-cases.json`), so neither can drift.
 *
 * His words, 2026-09-22:
 *
 *   "when we click tempo we should see: X-X-X-X and cursor be in the first one. we type, then the
 *    - is automatically there, and we are in the second digit and so on. and from letters only the
 *    X is allowed to use. when we typed all the 4 digits then we should not be able to type more.
 *    only to delete 1 by one and correct things or change."
 *
 * ⚠️ **THIS IS A BUILD FROM NOTHING ON THE WEB, WHICH IS WHY THE PHONE IS THE AUTHORITY.**
 * MEASURED before it: the web tempo field was a bare `<input value={exercise.tempo ?? ""}>` that
 * **accepted absolutely anything** — any length, any character, no mask, no auto-dash, no slots.
 * The phone meanwhile already held up to four counts, appended ONE character per press, dropped
 * the last on backspace, refused a fifth, and rendered them joined by `-`. Writing a second
 * implementation of his sentence here would have been vocabulary mismatch #25 by construction:
 * one `tempo` column, two producers, nothing comparing them.
 *
 * ⚠️ **THE ZERO PADDING IS HIS RULE**: `2-1` saved means `2-1-0-0`. A web field that padded
 * differently would put two different strings in one column for the same gesture.
 *
 * ⚠️ **AND THE ALPHABET IS BOUNDED BY THE ENGINE, MEASURED NOT ASSUMED.**
 * `TempoPlan.secondsPerRep` splits on `-`, `/` and `:`, accepts `X`/`x` as 0.5 s and any number,
 * and returns **null if any segment is unreadable** — so a stray letter does not merely look
 * wrong, it makes the whole tempo unreadable and the prescription loses its duration.
 */

/** Four: eccentric, pause, concentric, pause. Not a preference — it is what the notation is. */
export const TEMPO_SLOTS = 4;

export function isSlotCharacter(c: string): boolean {
  return /^[0-9Xx]$/.test(c);
}

/** The slots a displayed string holds. Empty segments are dropped, so `2-` and `2` are one slot. */
export function slotsIn(text: string): string[] {
  return text.split("-").filter((p) => p !== "");
}

/** Every state the FIELD may hold while it is being typed. */
export function accepts(text: string): boolean {
  if (text === "") return true;
  if (text.startsWith("-") || text.includes("--")) return false;
  const parts = text.split("-");
  const meaningful = parts.filter((p) => p !== "");
  if (meaningful.length > TEMPO_SLOTS) return false;
  if (parts.length > TEMPO_SLOTS) return false;
  return meaningful.every((p) => p.length === 1 && isSlotCharacter(p));
}

/** The text after a key press, or `null` when the press is REFUSED. */
export function typing(text: string, key: string): string | null {
  /* 🔴 A TEXT THIS RULE WOULD NOT ACCEPT IS NOT TYPED INTO. MEASURED: 220 live coach rows carry a
     tempo and most are UNDASHED (`2010`, `20X0`, `5/5`), which `slotsIn` reads as ONE four-character
     slot — so without this a keystroke produced `2010-3`. One backspace clears it and retyping
     migrates the row to the dashed form the engine can actually read. */
  if (!accepts(text)) return null;
  const parts = slotsIn(text);
  if (parts.length >= TEMPO_SLOTS || !isSlotCharacter(key)) return null;
  parts.push(key.toUpperCase());
  return parts.join("-");
}

/** His "only to delete 1 by one" — one SLOT, which is what a coach means by backspace here. */
export function deleting(text: string): string {
  const parts = slotsIn(text);
  parts.pop();
  return parts.join("-");
}

/** What gets STORED: padded to four with zeros, or nothing at all when empty. */
export function stored(text: string): string | null {
  /* 🔴 THE CLAUSE THAT STOPS A LIVE-DATA CORRUPTION. An undashed legacy tempo is ONE slot to
     `slotsIn`, so padding it would write `2010-0-0-0` — and the field's blur handler would have
     done that to any of the 220 live rows a coach merely focused and left. Padding may COMPLETE an
     entry this rule understands; it may never rewrite one it does not. */
  if (!accepts(text)) return text === "" ? null : text;
  const parts = slotsIn(text);
  if (parts.length === 0) return null;
  while (parts.length < TEMPO_SLOTS) parts.push("0");
  return parts.join("-");
}
